import { Button } from '@progress/kendo-react-buttons';
import { useCallback, useMemo, useState } from 'react';
import { KWELLO_ICON, KwelloSvgIcon } from '../../../components/icon/KwelloSvgIcon';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import {
  rollbackFilterChanges,
  selectAppliedFilters,
} from '../../../store/slices/common-filters.slice';
import { selectFiltersVisible, toggleFilters } from '../../../store/slices/filter-builder.slice';
import FilterFlyout from './FilterFlyout';
import { DatasetIDComponentProp } from '../../../../common/models/filter.model';
import { DATASET_INITIAL_ID } from '../../../../common/constants';
import { RootState } from '../../../../common/store';
import {
  ALIGNMENT_ROUTES,
  ANALYZE_ROUTES,
  COACH_ROUTES,
  INSIGHTS_ROUTES,
  INTERACTIONS_ROUTES,
  MY_IMPACT_MODULE_ROUTES,
} from 'routes/routes.const';
import { useCompareUrlWithProjectId } from 'common/hooks/route';
import { selectChartFilters } from 'common/store/slices/chart-filters.slice';
import CustomButton from 'common/components/KwelloButton/CustomButton';
import { AppliedFiltersElsie } from './AppliedFiltersElsie';
import { onlyUnique } from 'common/hooks/utils';
import { Badge } from '@progress/kendo-react-indicators';
import './FilterBar.scss';
import { PLANNING_ROUTES } from 'routes/routes.planning.const';

const SHOW_FILTER_BAR_PAGES = [
  ALIGNMENT_ROUTES.detail,
  ALIGNMENT_ROUTES.summary,
  ANALYZE_ROUTES.elsieai,
  ANALYZE_ROUTES.elsieaiV2,
  INSIGHTS_ROUTES.detail,
  INSIGHTS_ROUTES.summary,
  INTERACTIONS_ROUTES.root,
  ANALYZE_ROUTES.mir,
  ANALYZE_ROUTES.themeAnalytics,
  COACH_ROUTES.detail,
  COACH_ROUTES.summary,
  COACH_ROUTES.team_detail_interaction_kpis,
  COACH_ROUTES.team_detail_collected_responses,
  MY_IMPACT_MODULE_ROUTES.interaction_kpis,
  MY_IMPACT_MODULE_ROUTES.collected_responses,
  PLANNING_ROUTES.profiles,
];

export default function FilterBar({
  datasetId = DATASET_INITIAL_ID,
}: Readonly<DatasetIDComponentProp>) {
  const dispatch = useAppDispatch();
  const open = useAppSelector((state: RootState) => selectFiltersVisible(state, datasetId));
  const { compareUrl } = useCompareUrlWithProjectId();
  const showFilterBar = compareUrl(SHOW_FILTER_BAR_PAGES);
  const [hovered, setHovered] = useState(false);
  const filters = useAppSelector((root: RootState) => selectAppliedFilters(root, datasetId));
  const chartFilters = useAppSelector((root: RootState) => selectChartFilters(root, datasetId));

  const setOpen = useCallback(
    (next: boolean) => {
      dispatch(toggleFilters({ visible: next, datasetId }));
    },
    [datasetId, dispatch]
  );

  const keys = useMemo(
    () =>
      [
        ...Object.keys(filters || {}),

        ...chartFilters.map((chartFilter) => chartFilter.propertyName),
      ].filter(onlyUnique),

    [chartFilters, filters]
  );

  const openFilters = useCallback(() => {
    setOpen(true);
    dispatch(rollbackFilterChanges({ datasetId }));
  }, [datasetId, dispatch, setOpen]);

  // TODO Use compareUrl instead of location.pathname.endsWith('/elsieai2') for route check
  const isElsieAi2 = compareUrl(ANALYZE_ROUTES.elsieaiV2);

  if (!showFilterBar) {
    return null;
  }
  // TODO Use isElsieAi2 for conditional rendering
  if (isElsieAi2) {
    return (
      <div className="filter-container">
        <div
          className="cell filter-icon"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <CustomButton
            className="widget-ai-chat-input-send-icon svg-size-small"
            fillMode="solid"
            themeColor="base"
            onClick={openFilters}
            icon={KWELLO_ICON.FILTER_ICON}
          />
          <Badge
            size="medium"
            className="filter-badge"
            align={{ vertical: 'top', horizontal: 'end' }}
            position="edge"
          >
            {keys.length + 1}
          </Badge>
        </div>
        {hovered && (
          <div className="display-allppliedfilter">
            <div className="title"> AppliedFilters</div>
            <AppliedFiltersElsie />
          </div>
        )}
        {open && <FilterFlyout datasetId={datasetId} />}
      </div>
    );
  }
  return (
    <div className="nav-sidebar-wrap">
      <div className="nav-sidebar">
        <div className="nav-sidebar-buttons">
          <div className="cell">
            <Button onClick={() => openFilters()} fillMode={'flat'}>
              <KwelloSvgIcon
                className={'k-icon k-svg-icon k-button-icon'}
                icon={KWELLO_ICON.FILTER_ICON}
              />
              Filters
            </Button>
          </div>
        </div>
      </div>
      {open && <FilterFlyout datasetId={datasetId} />}
    </div>
  );
}
